<script>
import CurrentMeasurements from '@components/checkin/current-measurements.vue';
import Progressphotos from '@components/checkin/progress-photos.vue';
import UploadingCheckin from '@components/checkin/uploading-checkin.vue';
import { breakpointsMixin } from '@mixins/breakpointsMixin';
import {
  clientMethods,
  homeComputed,
  clientComputed,
  mediaUploadMethods,
  mediaUploadComputed,
  homeMethods,
} from '@state/helpers';
import _ from 'lodash';
import moment from 'moment';

export default {
  components: {
    CurrentMeasurements,
    Progressphotos,
    UploadingCheckin,
  },
  props: {
    stepProp: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  mixins: [breakpointsMixin],
  data() {
    return {
      // We collect all teh inputs in the parent component and upload all from the same place
      loading: false,
      weight: 0,
      chest: 0,
      hip: 0,
      waist: 0,
      step: this.stepProp && this.stepProp > 0 ? this.stepProp : 0, // This so we can view the next step if in mobile because we want to use a different checkin-detail.vue
      progressPhotos: {
        front: null,
        back: null,
        side: null,
      },
    };
  },
  computed: {
    ...homeComputed,
    ...clientComputed,
    ...mediaUploadComputed,
  },
  mounted() {},
  methods: {
    ...clientMethods,
    ...mediaUploadMethods,
    ...homeMethods,
    async uploadMedia(checkinObjectClone) {
      // Upload images and return the checkin object as a payload to submit the checkin
      const requestType = 'clients'; // To clients object in S3
      const type = 'cover'; // Image type
      const rowMediaArray = Object.keys(this.progressPhotos).map((key) => [String(key), this.progressPhotos[key]]); // Get only the flags have image to upload

      for (let i = 0; i < rowMediaArray.length; i++) {
        // Loop and upload images individually
        if (
          rowMediaArray[i] &&
          rowMediaArray[i].length === 2 &&
          rowMediaArray[i][1] &&
          _.has(rowMediaArray[i][1], 'file') && // Remember we want file form to upload
          rowMediaArray[i][1].file
        ) {
          await this.getS3UploadUrl({
            file: rowMediaArray[i][1].file,
            requestType,
            type,
            orgId: this.orgId,
            trainerId: this.trainerId,
          });
          await this.uploadPhoto({ model: requestType, option: type }).catch((e) => console.error(e));
          if (this.hasNewMedia(type, requestType).publicUrl) {
            // Check if image uploaded to S3 successfully
            checkinObjectClone.currentMediaAssets[rowMediaArray[i][0]].url = this.hasNewMedia(type, requestType).publicUrl;
          }
          this.setSignedPostData();
        }
      }
      return checkinObjectClone; // Return our ready checkin payload
    },
    async finish(type) {
      if (
        this.homeOverview &&
        this.homeOverview.checkInNeedingClientInput &&
        this.homeOverview.checkInNeedingClientInput.scheduledCheckIn_id
      ) {
        this.step++;
        try {
          const clientId = this.clientId;
          const checkinId = this.homeOverview.checkInNeedingClientInput.scheduledCheckIn_id;
          const checkinObject = await this.startCheckin({ clientId, checkinId });
          const checkinObjectClone = _.cloneDeep(checkinObject);

          checkinObjectClone.completionDate = moment().toISOString();
          checkinObjectClone.currentCheckIn.weight.value = this.weight;
          checkinObjectClone.currentCheckIn.chest.value = this.chest;
          checkinObjectClone.currentCheckIn.hip.value = this.hip;
          checkinObjectClone.currentCheckIn.waist.value = this.waist;

          if (type === 'skip') {
            // Do not upload any assets to S3 if skip clicked
            await this.updateCheckin({ clientId, checkinId, payload: { scheduledCheckIn: checkinObjectClone } }).then(
              async () => {
                await this.completeCheckin({ clientId, checkinId });
              }
            );
          }

          if (type === 'finish') {
            // If finish then do the upload
            const payload = await this.uploadMedia(checkinObjectClone);
            await this.updateCheckin({ clientId, checkinId, payload: { scheduledCheckIn: payload } }).then(async () => {
              // We update the data and then complete the checkin
              await this.completeCheckin({ clientId, checkinId });
            });
          }
          this.setHomeOverview({
            // This to upload our checkin call out in the calendar
            ...this.homeOverview,
            checkInNeedingClientInput: null,
          });
          this.$emit('checkin-completed'); // Close our modal
        } catch (e) {
          console.log(e);
          this.step--; // Undo loading and back to upload view
        }
      }
    },
  },
};
</script>

<template>
  <div :class="['checkin-details', { isMobile: !isDesktopView, firstStep: step === 0 }]">
    <div v-if="step === 0" :class="['begin-checkin']">
      <div class="begin-checkin-container">
        <h1 class="title">Time to Check-in</h1>
        <p>How are things going for you? Take some time to tell us about your personal progress.</p>
      </div>
      <div class="actions">
        <p class="close-text" @click.stop="$bvModal.hide('checkin-modal')">
          No, Not Right Now
        </p>
        <!-- Stay on the same modal if desktop otherwise go to mobile view -->
        <BaseButton class="button btn-primary large" @click.stop="isDesktopView ? step++ : $emit('show-mobile-checkin')">
          Begin Check-In
        </BaseButton>
      </div>
    </div>
    <CurrentMeasurements
      :class="{ isMobile: !isDesktopView }"
      v-if="step === 1"
      :weight="weight"
      @set-weight="weight = parseInt($event)"
      :waist="waist"
      @set-waist="waist = parseInt($event)"
      :hip="hip"
      @set-hip="hip = parseInt($event)"
      :chest="chest"
      @set-chest="chest = parseInt($event)"
      @next="step++"
      @back="step--"
    />
    <Progressphotos
      :class="{ isMobile: !isDesktopView }"
      v-if="step === 2"
      :progressPhotos="progressPhotos"
      @set-progress-photos-props="progressPhotos = $event"
      @next="step++"
      @back="step--"
      @finish="finish"
    />
    <UploadingCheckin v-if="step === 3" />
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.checkin-details {
  align-items: center;
  display: flex;
  justify-content: center;
  .begin-checkin {
    align-items: center;
    background-color: white;
    border-radius: $base-border-radius-md;
    box-shadow: 0 0 3px rgba(171, 171, 171, 0.5);
    display: flex;
    flex-direction: column;
    height: 32rem;
    justify-content: space-between;
    padding-top: 3rem;
    width: 90%;
    .begin-checkin-container {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      .title {
        color: $color-action;
        font-size: 26.78px;
        font-style: normal;
        font-weight: bold;
        line-height: 3.7rem;
        text-align: center;
        text-transform: capitalize;
      }
      p {
        font-size: 16px;
        left: auto;
        line-height: 23px;
        right: auto;
        text-align: center;
        width: 70%;
      }
    }
    .actions {
      align-items: center;
      display: flex;
      justify-content: center;
      width: 100%;
    }
    .close-text {
      bottom: 4.5rem;
      font-size: 1.7rem;
      font-weight: 600;
      line-height: 2.8rem;
      opacity: 0.5;
      position: absolute;
      text-align: center;
      text-transform: capitalize;
      &:hover {
        cursor: pointer;
      }
    }
    .btn-primary {
      bottom: -1.3rem;
      font-size: 1.75rem;
      position: absolute;
      width: 60%;
    }
  }
  &.isMobile {
    &:not(.firstStep) {
      background-color: white;
      height: 95vh;
    }
    .begin-checkin {
      height: 27rem;
      width: 100%;
      .begin-checkin-container {
        p {
          width: 80%;
        }
      }
    }
  }
}
</style>
